/**
 * ClusterSidebar — fixed-width sidebar with cluster list and selected node details.
 * Width stays constant; content scrolls vertically.
 */
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Copy, ShieldAlert, Sparkles, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import type { Dataset, ClusterId, GraphNodeDef } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface ClusterSidebarProps {
  dataset: Dataset;
  selectedClusterId: ClusterId | null;
  selectedNodeId: string | null;
  onSelect: (id: ClusterId) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  walletsCount: number;
  connectionsCount: number;
}

export default function ClusterSidebar({
  dataset,
  selectedClusterId,
  selectedNodeId,
  onSelect,
  collapsed,
  onToggleCollapse,
  walletsCount,
  connectionsCount,
}: ClusterSidebarProps) {
  const selectedNode = selectedNodeId ? dataset.graphNodeMap[selectedNodeId] : null;

  return (
    <motion.aside
      animate={{ width: collapsed ? 0 : 280 }}
      transition={{ duration: 0.35, ease: easeOut }}
      className="relative z-20 h-full flex-shrink-0 overflow-hidden border-r border-white/[0.08] bg-background/60 backdrop-blur-sm"
    >
      <div className="flex h-full w-[280px] flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4">
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Detected Clusters
          </span>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
          {dataset.clusters.map((cluster, i) => {
            const selected = selectedClusterId === cluster.id;
            return (
              <motion.button
                key={cluster.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.08 * i, ease: easeOut }}
                onClick={() => onSelect(cluster.id)}
                whileHover={{ y: -2 }}
                style={{
                  borderColor: selected ? `${cluster.color}80` : 'rgba(255,255,255,0.08)',
                  boxShadow: selected ? `0 8px 30px -10px ${cluster.color}66` : 'none',
                }}
                className="group w-full rounded-xl border bg-white/[0.03] p-3.5 text-left transition-colors hover:border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: cluster.color, boxShadow: `0 0 6px 1px ${cluster.color}99` }}
                    />
                    <span className="text-sm font-bold text-foreground">{cluster.name}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground/70">{cluster.age}</span>
                </div>

                <span
                  className="mt-2.5 inline-block rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                  style={{ borderColor: `${cluster.color}55`, color: cluster.color }}
                >
                  {cluster.categoryLabel}
                </span>

                <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>
                    PnL <span className="font-semibold text-foreground">{cluster.clusterPnl}</span>
                  </span>
                  <span>
                    WR <span className="font-semibold text-foreground">{cluster.clusterWinRate}%</span>
                  </span>
                </div>

                <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-white/[0.06]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${cluster.clusterWinRate}%` }}
                    transition={{ duration: 0.8, delay: 0.15 + 0.08 * i, ease: easeOut }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: cluster.color }}
                  />
                </div>
              </motion.button>
            );
          })}

          {/* Selected Node Details */}
          {selectedNode && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: easeOut }}
              className="mt-4 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4"
            >
              <div className="mb-3 flex items-center gap-2">
                <Wallet className="h-3.5 w-3.5 text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  Selected Wallet
                </span>
              </div>
              <NodeDetailsPanel node={selectedNode} dataset={dataset} />
            </motion.div>
          )}
        </div>

        <div className="border-t border-white/[0.08] px-4 py-3 text-[11px] text-muted-foreground/70">
          {walletsCount} wallets · {connectionsCount} connections · {dataset.clusters.length} clusters
        </div>
      </div>
    </motion.aside>
  );
}

function NodeDetailsPanel({ node, dataset }: { node: GraphNodeDef; dataset: Dataset }) {
  const copyAddress = () => {
    navigator.clipboard?.writeText(node.label).catch(() => undefined);
    toast.success('Wallet address copied');
  };

  const cluster = dataset.clusterMap[node.clusterId];

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
          <span className="font-mono text-sm text-foreground">{node.label}</span>
          <button
            onClick={copyAddress}
            className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-white/20 hover:text-foreground"
          >
            <Copy className="h-3 w-3" />
            Copy
          </button>
        </div>
        <span
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold"
          style={{ borderColor: `${cluster.color}55`, color: cluster.color }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: cluster.color }} />
          {cluster.name} {node.isHub ? 'Hub' : 'Member'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCell value={node.balance ?? '—'} label="Balance" />
        <StatCell value={`${node.winRate ?? 0}%`} label="Win Rate" />
        <StatCell value={node.realizedPnl ?? '—'} label="Realized PnL" />
        <StatCell value={node.unrealizedPnl ?? '—'} label="Unrealized PnL" />
      </div>

      <div className="space-y-2.5">
        <DetailRow icon={<TrendingUp className="h-3 w-3 text-[#14e0b0]" />} label="Trade Count" value={node.tradeCount?.toLocaleString() ?? '—'} />
        <DetailRow icon={<TrendingDown className="h-3 w-3 text-[#fb923c]" />} label="Avg Hold Time" value={node.avgHoldTime ?? '—'} />
        <DetailRow icon={<Wallet className="h-3 w-3 text-[#a78bfa]" />} label="Largest Position" value={node.largestPosition ?? '—'} />
        <DetailRow icon={<Sparkles className="h-3 w-3 text-primary" />} label="Connected Wallets" value={node.connectedWalletCount?.toString() ?? '—'} />
        <DetailRow icon={<ShieldAlert className="h-3 w-3 text-[#f472b6]" />} label="Confidence" value={`${node.confidenceScore ?? 0}%`} />
      </div>

      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Risk Indicators</p>
        <div className="flex flex-wrap gap-1.5">
          {(node.riskIndicators ?? []).map((r) => (
            <span key={r} className="rounded-md border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-[10px] font-medium text-foreground/80">
              {r}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-muted-foreground">First Seen</div>
          <div className="mt-0.5 font-semibold text-foreground">{node.firstSeen ?? '—'}</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-muted-foreground">Last Active</div>
          <div className="mt-0.5 font-semibold text-foreground">{node.lastActive ?? '—'}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-muted-foreground">Funding Source</div>
          <div className="mt-0.5 font-semibold text-foreground">{node.fundingSource ?? '—'}</div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          <div className="text-muted-foreground">Funding Method</div>
          <div className="mt-0.5 font-semibold text-foreground">{node.fundingMethod ?? '—'}</div>
        </div>
      </div>
    </div>
  );
}

function StatCell({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2.5">
      <div className="text-sm font-extrabold text-foreground">{value}</div>
      <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        {label}
      </div>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

export function SidebarExpandTab({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="absolute left-0 top-1/2 z-20 flex h-10 w-5 -translate-y-1/2 items-center justify-center rounded-r-md border border-l-0 border-white/10 bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
    >
      <ChevronRight className="h-3.5 w-3.5" />
    </button>
  );
}
